/**
 * Flattens a stored reimbursement policy into indented display rows.
 *
 * The policy an AI recommendation was judged under is persisted verbatim in the
 * recommendation context as `reimbursement_policy` — the composite tio-api built
 * by merging a partner's program and policy JSON. Its shape is authored per
 * partner, not by us: nesting runs several levels deep and mixes objects, arrays
 * of prose, and empty collections, and the keys differ between companies.
 *
 * So this walks the blob generically rather than reading named fields. A fixed
 * field list would render one partner correctly and silently drop everything the
 * next partner wrote.
 *
 * Values are shown as stored. Booleans become Yes/No, but numbers and strings
 * are never reformatted — the keys are partner-defined, so inferring a currency
 * or a date from a key name risks misstating the policy an approver is reading.
 */

/** Deepest level rendered as rows; anything beyond falls back to raw JSON. */
const MAX_DEPTH = 12;

export interface PolicyRow {
  /** Nesting level, 0 for top-level keys. Drives indentation only. */
  depth: number;
  label: string;
  /** A single stringified value. Mutually exclusive with `items`. */
  value?: string;
  /** A list of stringified values. Mutually exclusive with `value`. */
  items?: string[];
  /** True when the row only introduces the rows nested beneath it. */
  isHeading: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isScalar(value: unknown) {
  return (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

/**
 * `null` and `''` mean the partner left a key unfilled, which is worth showing —
 * a blank row would read as a rendering bug rather than as an unset policy.
 */
function formatScalar(value: unknown): string {
  if (value === null || value === undefined) return 'Not specified';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  const text = String(value).trim();
  return text.length > 0 ? text : 'Not specified';
}

/**
 * Only the first character is uppercased — the rest is left alone so an acronym
 * the partner capitalised (`GPA`, `CEU`) survives instead of becoming `Gpa`.
 */
function humanizeKey(key: string) {
  const spaced = key.replace(/[_-]+/g, ' ').trim();
  return spaced.length > 0 ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key;
}

function rowsFor(label: string, value: unknown, depth: number): PolicyRow[] {
  if (depth >= MAX_DEPTH) {
    return [
      { depth, label, value: JSON.stringify(value) ?? formatScalar(value), isHeading: false },
    ];
  }

  if (Array.isArray(value)) {
    // An empty collection is a statement ("nothing is ineligible"), so it gets a
    // row of its own rather than being dropped as if the key never existed.
    if (value.length === 0) return [{ depth, label, value: 'None', isHeading: false }];

    if (value.every(isScalar)) {
      return [{ depth, label, items: value.map(formatScalar), isHeading: false }];
    }

    return [
      { depth, label, isHeading: true },
      ...value.flatMap((entry, index) => rowsFor(`${index + 1}.`, entry, depth + 1)),
    ];
  }

  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) return [{ depth, label, value: 'None', isHeading: false }];

    return [
      { depth, label, isHeading: true },
      ...entries.flatMap(([key, nested]) => rowsFor(humanizeKey(key), nested, depth + 1)),
    ];
  }

  return [{ depth, label, value: formatScalar(value), isHeading: false }];
}

export function flattenPolicy(policy: unknown): PolicyRow[] {
  if (!isPlainObject(policy)) return [];

  return Object.entries(policy).flatMap(([key, value]) => rowsFor(humanizeKey(key), value, 0));
}
